/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton } from '@fluentui/react/lib/Button';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextSlots } from '@fluentui/react/lib/Text';
import { ITextFieldProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CopyButton } from 'src/core/components/CopyButton';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getStylist, important } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { EndpointUrlParams } from 'src/core/utils/endpointBuilder/types';
import {
    ChangeEndpointUrlParametersModal,
    ChangeEndpointUrlParametersModalDto
} from 'src/luis/appShell/managePane/azureAccountsPage/components/ChangeEndpointUrlParametersModal';
import { QueryExampleDto } from 'src/luis/appShell/managePane/azureAccountsPage/components/QueryExampleDto';

export type AzureCsAccountCardProps = {
    /**
     * The Azure Resource fields
     */
    azureCsAccount: AzureCsAccountStore;

    /**
     * The builder function for endpoint url.
     */
    buildEndpointUrl: (urlParams: EndpointUrlParams) => string;

    /**
     * Fields for constructing the example query
     */
    queryExample?: QueryExampleDto;

    /**
     * Show/hide the un-assign Action Button
     */
    showUnAssignButton?: boolean;

    /**
     * Callback function for the un-assign button
     */
    onUnAssignButtonClick?: (id: AzureCsAccountStore['id']) => void;
};

const messages = defineMessages({
    unAssignKeyLabel: {
        id: 'keys.resourceCard.unAssignKeyLabel',
        defaultMessage: 'Un-assign key'
    },
    starterLabel: {
        id: 'keys.resourceCard.starterLabel',
        defaultMessage: 'Starter'
    },
    starterInstructions: {
        id: 'keys.resourceCard.starterInstructions',
        defaultMessage: `When you create a LUIS account, you receive a starter resource with 1000 free endpoint queries.
Once those endpoint queries are used, you will need to migrate to an Azure resource. Migrate now.`
    },
    migrateNow: {
        id: 'keys.resourceCard.migrateNow',
        defaultMessage: 'Migrate now.'
    },
    resourceGroupLabel: {
        id: 'keys.resourceCard.resourceGroupLabel',
        defaultMessage: 'Resource group:'
    },
    locationLabel: {
        id: 'keys.resourceCard.locationLabel',
        defaultMessage: 'Location:'
    },
    primaryKeyLabel: {
        id: 'keys.resourceCard.primaryKeyLabel',
        defaultMessage: 'Primary Key:'
    },
    secondaryKeyLabel: {
        id: 'keys.resourceCard.secondaryKeyLabel',
        defaultMessage: 'Secondary Key:'
    },
    endpointUrlLabel: {
        id: 'keys.resourceCard.endpointUrlLabel',
        defaultMessage: 'Endpoint URL:'
    },
    pricingTierLabel: {
        id: 'keys.resourceCard.pricingTierLabel',
        defaultMessage: 'Pricing Tier:'
    },
    exampleQueryLabel: {
        id: 'keys.resourceCard.exampleQueryLabel',
        defaultMessage: 'Example Query:'
    },
    changeQueryParametersLabel: {
        id: 'keys.resourceCard.changeQueryParametersLabel',
        defaultMessage: 'Change query parameters'
    },
    canNotListKeyStrings: {
        id: 'keys.resourceCard.canNotListKeyStrings',
        defaultMessage: 'Your role ( {displayedUserRole} ) is not permitted to view the Azure resource keys'
    }
});

const { styleComponent, getClassName, styleDiv } = getStylist('AzureCsAccountCard');

const FullWidthDiv = styleDiv('FullWidthDiv', { width: '100%' });
const Section = styleComponent<IStackProps>(Stack)('Section', theme => ({ width: theme.sectionWidth }));
const CardInfoItemContainer = styleComponent<IStackProps>(Stack)('CardInfoItemContainer', theme => ({
    minHeight: theme.cardInfoItemHeight
}));
const ResourceText = styleComponent<LuisTextProps>(LuisText)('ResourceText', theme => ({ paddingLeft: theme.resourceTextLeftPadding }));
const ResourceLabel = styleComponent<ILabelProps>(Label)('resourceLabel', theme => ({
    minWidth: theme.resourceLabelMinWidth,
    fontSize: theme.themeFontSize,
    fontWeight: theme.resourceLabelFontWeight as Property.FontWeight
}));
const ExampleQuery = styleComponent<IStackProps>(Stack)('QueryExample', theme => ({
    width: theme.queryExampleWidth,
    paddingTop: theme.queryExamplePaddingTop
}));

const modalNudgeWrapperStyleFix = getClassName('NudgeWrapperFix', important({ marginTop: 0 }));
const flexStyles = classNamesFunction<ITextFieldProps, ITextFieldStyles>()({ root: { flex: 1 } });

/**
 * Component representing all attributes of one authoring resource
 *
 * @param props The input to the component.
 */
export const AzureCsAccountCard = observer((props: AzureCsAccountCardProps) => {
    const { azureCsAccount, showUnAssignButton, onUnAssignButtonClick, buildEndpointUrl } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const appId = rootStore.currentLuisAppStore?.id;
    const lastProdPublishingDate = rootStore.currentLuisAppStore?.publishSlots.production?.lastPublishedDate;
    const lastStagingPublishingDate = rootStore.currentLuisAppStore?.publishSlots.staging?.lastPublishedDate;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canListResourceKeys } = mapUserRoleToUserActions(userRole);

    const [urlParams, setUrlParams] = React.useState<ChangeEndpointUrlParametersModalDto>({
        slot: 'production',
        verbose: true,
        log: true,
        allIntents: true,
        spellCheck: ''
    });
    const [query, setQuery] = React.useState(
        buildEndpointUrl({
            base: azureCsAccount.endpoint,
            appId,
            subscriptionKey: azureCsAccount.keyStrings.key1,
            ...urlParams
        })
    );
    const [isChangeParametersModalHidden, setIsChangeParametersModalHidden] = React.useState(true);

    const isStarterPredictionResource = azureCsAccount.name.toString() === localize(messages.starterLabel);

    React.useEffect(() => {
        const lastPublishedSlot = (lastStagingPublishingDate || '') > (lastProdPublishingDate || '') ? 'staging' : 'production';

        if (urlParams.slot !== lastPublishedSlot) {
            onEndpointUrlParamsChange({ ...urlParams, slot: lastPublishedSlot });
        }
    }, [lastProdPublishingDate, lastStagingPublishingDate]);

    /**
     * Changes the params of the endpoint url and reflects that in the query.
     */
    const onEndpointUrlParamsChange = (newParams: ChangeEndpointUrlParametersModalDto) => {
        setUrlParams(newParams);
        setQuery(
            buildEndpointUrl({
                base: azureCsAccount.endpoint,
                appId,
                subscriptionKey: azureCsAccount.keyStrings.key1,
                ...newParams
            })
        );

        setIsChangeParametersModalHidden(true);
    };

    const textStyles = classNamesFunction<ITextFieldProps, IComponentStyles<ITextSlots>>()({
        root: { fontStyle: 'italic' }
    });

    return (
        <Section tokens={{ childrenGap: 24 }}>
            <Stack tokens={{ childrenGap: 5 }}>
                <LuisText variant="large" fontWeight="bold" as="h2">
                    {azureCsAccount.name}
                </LuisText>

                {showUnAssignButton && (
                    <Stack.Item>
                        <ActionButton iconProps={{ iconName: 'PlugDisconnected' }} onClick={() => onUnAssignButtonClick(azureCsAccount.id)}>
                            {localize(messages.unAssignKeyLabel)}
                        </ActionButton>
                    </Stack.Item>
                )}

                {isStarterPredictionResource && (
                    <LuisText variant="small">
                        {localize(messages.starterInstructions)}

                        <Link>{localize(messages.migrateNow)}</Link>
                    </LuisText>
                )}
            </Stack>

            <Stack>
                {azureCsAccount.resourceGroupName && (
                    <CardInfoItemContainer horizontal verticalAlign="center">
                        <ResourceLabel>{localize(messages.resourceGroupLabel)}</ResourceLabel>
                        <ResourceText variant="small">{azureCsAccount.resourceGroupName}</ResourceText>
                    </CardInfoItemContainer>
                )}

                <CardInfoItemContainer horizontal verticalAlign="center">
                    <ResourceLabel> {localize(messages.locationLabel)}</ResourceLabel>
                    <ResourceText variant="small">{azureCsAccount.region}</ResourceText>
                </CardInfoItemContainer>

                <CardInfoItemContainer horizontal verticalAlign="center">
                    <ResourceLabel>{localize(messages.primaryKeyLabel)}</ResourceLabel>
                    <CopyButton
                        textToCopy={azureCsAccount.keyStrings.key1}
                        label={localize(messages.primaryKeyLabel)}
                        disabled={!canListResourceKeys}
                    />
                    {canListResourceKeys ? (
                        <TruncatedText variant="small">{azureCsAccount.keyStrings.key1}</TruncatedText>
                    ) : (
                        <TruncatedText
                            title={localize(messages.canNotListKeyStrings, { displayedUserRole })}
                            variant="small"
                            maxWidth={400}
                            fontWeight="bold"
                            styles={textStyles}>
                            {localize(messages.canNotListKeyStrings, { displayedUserRole })}
                        </TruncatedText>
                    )}
                </CardInfoItemContainer>

                {azureCsAccount.keyStrings.key2 && (
                    <CardInfoItemContainer horizontal verticalAlign="center">
                        <ResourceLabel>{localize(messages.secondaryKeyLabel)}</ResourceLabel>
                        <CopyButton
                            textToCopy={azureCsAccount.keyStrings.key2}
                            label={localize(messages.secondaryKeyLabel)}
                            disabled={!canListResourceKeys}
                        />
                        {canListResourceKeys ? (
                            <TruncatedText variant="small">{azureCsAccount.keyStrings.key2}</TruncatedText>
                        ) : (
                            <TruncatedText
                                title={localize(messages.canNotListKeyStrings, { displayedUserRole })}
                                variant="small"
                                maxWidth={400}
                                fontWeight="bold"
                                styles={textStyles}>
                                {localize(messages.canNotListKeyStrings, { displayedUserRole })}
                            </TruncatedText>
                        )}
                    </CardInfoItemContainer>
                )}

                <CardInfoItemContainer horizontal verticalAlign="center">
                    <ResourceLabel>{localize(messages.endpointUrlLabel)}</ResourceLabel>
                    <CopyButton textToCopy={azureCsAccount.endpoint} label={localize(messages.endpointUrlLabel)} />
                    <TruncatedText variant="small" maxWidth={400}>
                        {azureCsAccount.endpoint}
                    </TruncatedText>
                </CardInfoItemContainer>

                <CardInfoItemContainer horizontal verticalAlign="center">
                    <ResourceLabel>{localize(messages.pricingTierLabel)}</ResourceLabel>
                    <ResourceText variant="small">{azureCsAccount.sku.code}</ResourceText>
                    &nbsp;
                    {azureCsAccount.sku?.name && <LuisText variant="small">({azureCsAccount.sku.name})</LuisText>}
                </CardInfoItemContainer>

                {azureCsAccount.kind !== 'LUIS.Authoring' && (
                    <CardInfoItemContainer horizontal verticalAlign="start">
                        <ResourceLabel>{localize(messages.exampleQueryLabel)}</ResourceLabel>

                        <FullWidthDiv>
                            <ExampleQuery horizontal>
                                <LuisTextField
                                    styles={flexStyles}
                                    ariaLabel={localize(messages.exampleQueryLabel)}
                                    readOnly
                                    multiline
                                    value={query}
                                />
                                <CopyButton textToCopy={query} label={localize(messages.exampleQueryLabel)} />
                            </ExampleQuery>

                            <LuisText variant="small">
                                <Link as="a" onClick={() => setIsChangeParametersModalHidden(false)}>
                                    {localize(messages.changeQueryParametersLabel)}
                                </Link>
                            </LuisText>
                        </FullWidthDiv>
                    </CardInfoItemContainer>
                )}
            </Stack>

            <div className={modalNudgeWrapperStyleFix}>
                <ChangeEndpointUrlParametersModal
                    data={urlParams}
                    onSubmit={onEndpointUrlParamsChange}
                    hidden={isChangeParametersModalHidden}
                    onDismiss={() => setIsChangeParametersModalHidden(true)}
                />
            </div>
        </Section>
    );
});
